import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns-tz";
import { BrazilDate } from "../../../../utils/BrazilDate";
import { useAuth } from "../../../../context/AuthContext";
import { entregaService } from "../../../../services/entregaService";
import { lojaService } from "../../../../services/lojaService";
import { useEntregas } from "../../../../hooks/useEntrega";
import { useFilaEntregas } from "../../../../hooks/useFilaEntrega";
import { buildFilaEntregaDiaAtualFilters } from "../../../../services/filaEntregaService";
import { Loja } from "../../../../types/loja";
import { parseHHMMSSToSeconds } from "../utils/formatTime";
import { useRankingEvents } from "../../../../hooks/useRankingEvents";
import { RANKING_SSE_QUERY_KEYS } from "../../../../constants/rankingEventQueryKeys";
import { useEntregadorChamado } from "../../../../hooks/useEntregadorChamado";

import { FilaEntregador, EntregadorStat, ChamadoAtual } from "../types";

interface RankingEntregaContextData {
  aguardando: FilaEntregador[];
  emEntrega: FilaEntregador[];
  top3Entregadores: EntregadorStat[];
  lojas: Loja[];
  isLoading: boolean;
  selectedLojaId?: number;
  showLojaSelector: boolean;
  dateLabel: string;
  selectedLojaNome?: string;
  chamadoAtual: ChamadoAtual | null;
  segundosRestantes: number;
  toggleLojaSelector: () => void;
  selectAllLojas: () => void;
  selectLoja: (id: number) => void;
}

interface RankingEntregaProviderProps {
  children: React.ReactNode;
}

const RankingEntregaContext = createContext<RankingEntregaContextData>(
  {} as RankingEntregaContextData
);

export const RankingEntregaProvider: React.FC<RankingEntregaProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    () => user?.lojaId
  );
  const [showLojaSelector, setShowLojaSelector] = useState(false);
  const [chamadoAtual, setChamadoAtual] = useState<ChamadoAtual | null>(null);
  const [segundosRestantes, setSegundosRestantes] = useState(15);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const getTodayDate = useCallback(() => {
    const now = BrazilDate.now();
    const timezone = BrazilDate.getTimezone();
    return format(now, "dd/MM/yyyy HH:mm", { timeZone: timezone });
  }, []);

  const dateLabel = getTodayDate();

  const { data: lojas = [], isLoading: isLoadingLojas } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const rankingDiaFilters = useMemo(
    () => ({ lojaId: selectedLojaId ?? null }),
    [selectedLojaId]
  );

  const { data: rankingDiaData, isLoading: isLoadingRankingDia } = useQuery({
    queryKey: ["ranking-dia-entrega", rankingDiaFilters],
    queryFn: () => entregaService.getRankingDia(rankingDiaFilters),
    refetchInterval: false,
  });

  useRankingEvents("entrega", selectedLojaId, {
    queryKeys: RANKING_SSE_QUERY_KEYS.rankingEntrega,
  });

  const handleEntregadorChamado = useCallback(
    (nome: string, sobrenome: string) => {
      setChamadoAtual({ nome, sobrenome });
      setSegundosRestantes(15);

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      const audio = new Audio("/entregas.mp3");
      audio.volume = 0.7;
      audio.play().catch((error) => {
        console.error("Erro ao reproduzir áudio:", error);
      });
      audioRef.current = audio;

      setTimeout(() => {
        if ("speechSynthesis" in window) {
          speechSynthesis.cancel();
          const utterance = new SpeechSynthesisUtterance(
            `${nome} ${sobrenome}, vá até a cozinha retirar seu pedido!`
          );
          utterance.lang = "pt-BR";
          utterance.rate = 0.9;
          utterance.pitch = 1.1;
          speechSynthesis.speak(utterance);
        }
      }, 3000);

      const interval = setInterval(() => {
        setSegundosRestantes((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setChamadoAtual(null);
            return 15;
          }
          return prev - 1;
        });
      }, 1000);

      timerRef.current = interval;
    },
    []
  );

  useEntregadorChamado(handleEntregadorChamado, selectedLojaId);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if ("speechSynthesis" in window) {
        speechSynthesis.cancel();
      }
    };
  }, []);

  const entregasFilters = useMemo(
    () => ({
      lojaId: selectedLojaId || undefined,
      status: "ativo",
    }),
    [selectedLojaId]
  );

  const filaFilters = useMemo(
    () =>
      selectedLojaId
        ? buildFilaEntregaDiaAtualFilters(selectedLojaId, {
            status: "em_andamento",
          })
        : undefined,
    [selectedLojaId]
  );

  const { data: entregasData, isLoading: isLoadingEntregas } =
    useEntregas(entregasFilters);

  const { data: filaEntregas = [], isLoading: isLoadingFila } =
    useFilaEntregas(filaFilters);

  const isLoading =
    isLoadingLojas || isLoadingRankingDia || isLoadingEntregas || isLoadingFila;

  const top3Entregadores: EntregadorStat[] = useMemo(() => {
    if (!rankingDiaData?.ranking) return [];

    return rankingDiaData.ranking.slice(0, 3).map((item) => ({
      id: item.usuarioId,
      nome: item.nome,
      tempoMedio: parseHHMMSSToSeconds(item.tempoMedio),
      entregas: item.quantidadeEntregas,
    }));
  }, [rankingDiaData]);

  const aguardando: FilaEntregador[] = useMemo(() => {
    if (!rankingDiaData?.aguardando) return [];

    return rankingDiaData.aguardando
      .map((item) => ({
        id: item.id,
        entregadorId: item.entregadorId,
        nome: item.nome,
        dataEntrada:
          typeof item.dataEntrada === "string"
            ? item.dataEntrada
            : new Date(item.dataEntrada).toISOString(),
      }))
      .sort(
        (a, b) =>
          new Date(a.dataEntrada).getTime() - new Date(b.dataEntrada).getTime()
      )
      .map((item, index) => ({ ...item, posicao: index + 1 }));
  }, [rankingDiaData]);

  const emEntrega: FilaEntregador[] = useMemo(() => {
    const entregadoresUnicos = new Map<number, FilaEntregador>();

    if (entregasData?.data) {
      entregasData.data.forEach((entrega) => {
        if (
          entrega.status === "ativo" &&
          !entrega.dataFim &&
          entrega.entregador &&
          !entregadoresUnicos.has(entrega.entregador.id)
        ) {
          entregadoresUnicos.set(entrega.entregador.id, {
            id: entrega.id,
            entregadorId: entrega.entregador.id,
            nome: entrega.entregador.nome,
            dataEntrada: entrega.dataInicio,
          });
        }
      });
    }

    if (filaEntregas && filaEntregas.length > 0) {
      filaEntregas.forEach((fila) => {
        if (
          fila.status === "em_andamento" &&
          fila.entregador &&
          !entregadoresUnicos.has(fila.entregadorId)
        ) {
          entregadoresUnicos.set(fila.entregadorId, {
            id: fila.id || 0,
            entregadorId: fila.entregadorId,
            nome: fila.entregador.nome,
            dataEntrada: fila.dataFila,
          });
        }
      });
    }

    return Array.from(entregadoresUnicos.values());
  }, [entregasData, filaEntregas]);

  const selectedLojaNome = useMemo(
    () => lojas.find((l) => l.id === selectedLojaId)?.nome,
    [lojas, selectedLojaId]
  );

  const toggleLojaSelector = useCallback(() => {
    setShowLojaSelector((prev) => !prev);
  }, []);

  const selectAllLojas = useCallback(() => {
    setSelectedLojaId(undefined);
    setShowLojaSelector(false);
  }, []);

  const selectLoja = useCallback((id: number) => {
    setSelectedLojaId(id);
    setShowLojaSelector(false);
  }, []);

  return (
    <RankingEntregaContext.Provider
      value={{
        aguardando,
        emEntrega,
        top3Entregadores,
        lojas,
        isLoading,
        selectedLojaId,
        showLojaSelector,
        dateLabel,
        selectedLojaNome,
        chamadoAtual,
        segundosRestantes,
        toggleLojaSelector,
        selectAllLojas,
        selectLoja,
      }}
    >
      {children}
    </RankingEntregaContext.Provider>
  );
};

export const useRankingEntrega = (): RankingEntregaContextData => {
  const context = useContext(RankingEntregaContext);
  if (!context) {
    throw new Error(
      "useRankingEntrega must be used within RankingEntregaProvider"
    );
  }
  return context;
};
